/**
 * Quarantine Zone Management
 * ─────────────────────────────────────────────────────────────────────────────
 * Shows all Mill Lots whose bales are physically received and stored in godown
 * but are AWAITING QC clearance before they can be issued to production.
 *
 * Real-world workflow:
 *   1. Bales arrive → GRN punched → Godown assigned for physical unloading
 *   2. Samples taken from that godown → sent to QC lab
 *   3. QC lab tests (HVI, moisture, fibre properties)
 *   4. QC Approved → bales released for production issuance
 *
 * "Quarantine" means: physically in the godown but BLOCKED from production
 * issuance until QC clears them.
 *
 * Status:
 *   PENDING → Samples not yet tested / results awaited
 *   HOLD    → QC flagged an issue — needs review (critical)
 */
import {
  Alert, App, Badge, Button, Card, Col, Empty,
  Row, Space, Statistic, Tag, Typography,
} from 'antd'
import {
  ClockCircleOutlined, ExperimentOutlined, ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface QuarantineLot {
  mill_lot_id: string
  mill_lot_no: string | null
  cotton_variety: string | null
  crop_year: string | null
  supplier_lot_ref: string | null
  total_bales_in_quarantine: number
  qc_pending: number
  qc_hold: number
  current_zone: string | null
  days_waiting: number
  oldest_arrival: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function QuarantineZonePage() {
  const { plantId } = usePlantId()
  const navigate = useNavigate()
  const { message } = App.useApp()

  const { data: lots = [], isLoading, refetch } = useQuery<QuarantineLot[]>({
    queryKey: ['quarantine-zone', plantId],
    queryFn: () =>
      apiClient
        .get('/bales/quarantine-zone', { params: { plant_id: plantId } })
        .then(r => r.data),
    enabled: !!plantId,
    refetchInterval: 60_000,
  })

  // KPIs
  const totalBales   = lots.reduce((s, l) => s + l.total_bales_in_quarantine, 0)
  const holdLots     = lots.filter(l => l.qc_hold > 0).length
  const pendingLots  = lots.filter(l => l.qc_pending > 0 && l.qc_hold === 0).length
  const maxDays      = lots.reduce((m, l) => Math.max(m, l.days_waiting), 0)

  return (
    <div>
      <PageHeader
        title="Quarantine Zone"
        subtitle="Bales physically received & stored — awaiting QC lab results before issuance to production"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
            <Button
              type="primary"
              icon={<ExperimentOutlined />}
              onClick={() => navigate('/bale-management/qc')}
            >
              Go to QC Testing
            </Button>
          </Space>
        }
      />

      {/* KPI strip */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Lots in Quarantine</span>}
              value={lots.length}
              valueStyle={{ color: '#faad14', fontWeight: 700 }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #ff4d4f' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>On Hold (Critical)</span>}
              value={holdLots}
              valueStyle={{ color: '#ff4d4f', fontWeight: 700 }}
              prefix={<WarningOutlined />}
              suffix={<span style={{ fontSize: 12 }}>lots</span>}
            />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #1677ff' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Total Bales Held</span>}
              value={totalBales}
              valueStyle={{ color: '#1677ff', fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: maxDays > 7 ? '3px solid #ff4d4f' : '3px solid #8c8c8c' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Longest Wait (days)</span>}
              value={maxDays}
              valueStyle={{ color: maxDays > 7 ? '#ff4d4f' : '#595959', fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Hold alert */}
      {holdLots > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 12 }}
          message={`${holdLots} Mill Lot(s) are ON HOLD — QC has flagged issues. Review immediately.`}
        />
      )}

      {maxDays > 7 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${pendingLots + holdLots} lot(s) have bales waiting over 7 days for QC clearance. Expedite testing.`}
        />
      )}

      {/* Lot cards */}
      {!plantId ? (
        <Empty description="Select a plant" />
      ) : isLoading ? (
        <Card loading />
      ) : lots.length === 0 ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: '#52c41a' }}>
                ✓ No bales in quarantine — all lots have passed QC
              </span>
            }
          />
        </Card>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {lots.map(lot => {
            const isHold = lot.qc_hold > 0
            const borderColor = isHold ? '#ff4d4f' : '#faad14'

            return (
              <Card
                key={lot.mill_lot_id}
                size="small"
                style={{ borderLeft: `4px solid ${borderColor}` }}
                title={
                  <Space wrap>
                    <Badge
                      count={lot.total_bales_in_quarantine}
                      color={isHold ? '#ff4d4f' : '#faad14'}
                    />
                    <Text code style={{ fontSize: 13 }}>{lot.mill_lot_no || '—'}</Text>
                    {lot.cotton_variety && (
                      <Tag color="blue" style={{ fontSize: 11 }}>
                        {lot.cotton_variety}{lot.crop_year ? ` (${lot.crop_year})` : ''}
                      </Tag>
                    )}
                    {isHold && (
                      <Tag color="red" icon={<WarningOutlined />} style={{ fontSize: 11 }}>
                        ON HOLD
                      </Tag>
                    )}
                    {!isHold && (
                      <Tag color="orange" icon={<ClockCircleOutlined />} style={{ fontSize: 11 }}>
                        QC PENDING
                      </Tag>
                    )}
                  </Space>
                }
                extra={
                  <Button
                    size="small"
                    type="primary"
                    icon={<ExperimentOutlined />}
                    onClick={() => navigate('/bale-management/qc')}
                  >
                    Start QC
                  </Button>
                }
              >
                <Row gutter={16}>
                  <Col xs={24} sm={16}>
                    <Space wrap size={8}>
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>QC Pending </Text>
                        <Tag color="orange">{lot.qc_pending} bales</Tag>
                      </div>
                      {lot.qc_hold > 0 && (
                        <div>
                          <Text type="secondary" style={{ fontSize: 11 }}>On Hold </Text>
                          <Tag color="red">{lot.qc_hold} bales</Tag>
                        </div>
                      )}
                      {lot.current_zone && (
                        <div>
                          <Text type="secondary" style={{ fontSize: 11 }}>Current Zone </Text>
                          <Tag color="geekblue">{lot.current_zone}</Tag>
                        </div>
                      )}
                      {lot.supplier_lot_ref && (
                        <div>
                          <Text type="secondary" style={{ fontSize: 11 }}>Supplier Lot </Text>
                          <Text style={{ fontSize: 12 }}>{lot.supplier_lot_ref}</Text>
                        </div>
                      )}
                    </Space>
                  </Col>
                  <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                    <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                      {lot.days_waiting > 0 && (
                        <Text
                          type={lot.days_waiting > 7 ? 'danger' : 'warning'}
                          style={{ fontSize: 12 }}
                        >
                          ⏱ Waiting {lot.days_waiting} day{lot.days_waiting !== 1 ? 's' : ''}
                        </Text>
                      )}
                      {lot.oldest_arrival && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Arrived: {dayjs(lot.oldest_arrival).format('DD-MMM-YYYY')}
                        </Text>
                      )}
                    </Space>
                  </Col>
                </Row>

                <div style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  background: isHold ? '#fff1f0' : '#fffbe6',
                  borderRadius: 4,
                  border: `1px solid ${isHold ? '#ffccc7' : '#ffe58f'}`,
                }}>
                  <Text style={{ fontSize: 12, color: isHold ? '#a8071a' : '#874d00' }}>
                    {isHold
                      ? '⚠️ This lot is ON HOLD. QC has flagged an issue — resolve before releasing to production.'
                      : '🔒 Bales are in the godown. Samples sent to QC lab. Issuance to production is blocked until QC approves this lot.'}
                  </Text>
                </div>
              </Card>
            )
          })}
        </Space>
      )}
    </div>
  )
}
